import { useMemo } from 'react'
import { EMkStorageKeys } from '@utils/DataStore'
import { ProtectedDataStore } from '@utils/ProtectedDataStore'
import { IMkAuthorizedSession, IMkAuthenticatedSession } from '@auth/types'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { Brand } from '@typescript'

/**
 * Custom hook to generate authentication headers for API requests
 * @returns An object containing authentication headers
 */
export const useAuthHeaders = ({ session }: { session: IMkAuthenticatedSession | null } = { session: null }): Record<string, string> => {
  // Try to get session from useAvailability hook if not provided
  const { session: authSession } = useAuthentication()

  // Try to get session from ProtectedDataStore if still not available
  const storeSession = ProtectedDataStore.getItem<IMkAuthorizedSession>(EMkStorageKeys.MAKO_SESSION) || null

  // Use the provided session, or fall back to the one from useAvailability, or finally from ProtectedDataStore
  const effectiveSession = session || authSession || storeSession || null

  const headers = useMemo(() => {
    const baseHeaders = {
      'Content-Type': 'application/json',
      'X-JWT-Token': '',
      'X-JWT-Member-Token': '',
      'Access-Code': '',
    }

    if (effectiveSession) {
      const { jwtToken, memberAccessToken, authBrand, accessCode, authorizedUser } = effectiveSession as IMkAuthorizedSession
      const brandId = Brand.getId(authBrand)
      const brandKey = Brand.getKey(authBrand)

      return {
        ...baseHeaders,
        'x-brandid': brandId,
        'x-brandkey': brandKey,
        'X-JWT-Token': jwtToken || '',
        'X-JWT-Member-Token': memberAccessToken || '',
        'Access-Code': accessCode || '',
        'X-USERID': authorizedUser?.username || '',
      }
    }

    return baseHeaders
  }, [effectiveSession])

  return headers
}

export type AuthHeaders = ReturnType<typeof useAuthHeaders>
